import ModulePageLayout from "./ModulePageLayout";
import { Link } from "react-router-dom";
import {
  Factory,
  ClipboardList,
  Boxes,
  ShieldCheck,
  ScanLine,
  BarChart3,
} from "lucide-react";

const CAPABILITIES = [
  {
    icon: ClipboardList,
    title: "Recipes & formulas",
    body: "AI-suggested optimisation, allergen tracking and automatic cost-per-unit roll-up at the recipe or formula level.",
  },
  {
    icon: Boxes,
    title: "Multi-level BOM",
    body: "Raw, semi-finished and finished goods are tracked as a single versioned BOM with cost roll-up at every level.",
  },
  {
    icon: Factory,
    title: "Production planning",
    body: "Production orders, capacity planning and the schedule calendar stay synced with inventory and finance.",
  },
  {
    icon: ShieldCheck,
    title: "Quality control",
    body: "Per-batch checklists, pass/fail outcomes, non-conformance reports and AI trend analysis built into the production flow.",
  },
  {
    icon: ScanLine,
    title: "Full traceability",
    body: "Lot numbers, expiry tracking and one-click recall across the entire supply chain, from supplier to shipped order.",
  },
  {
    icon: BarChart3,
    title: "Production costs",
    body: "Labour, materials and overhead combine into a real-time view of margin per product.",
  },
];

const INDUSTRIES = [
  "Food & beverage producers",
  "Cosmetics brands",
  "Pharma & supplement SMEs",
  "Light manufacturing teams",
];

export default function ManufacturingModule() {
  return (
    <ModulePageLayout
      title="Manufacturing module — Zynthoro AI ERP for SMEs"
      description="Zynthoro's manufacturing module runs recipes, BOMs, work orders, quality control and lot traceability inside one AI-native ERP for European SMEs."
      breadcrumbLabel="Manufacturing"
      heroEyebrow="Modules · Manufacturing"
      heroHeading="The production stack for European SMEs."
      heroBody="Recipes, BOMs, work orders, quality checks and full lot traceability — Zynthoro's manufacturing capability lives inside one AI-native ERP, so production, stock and finance stay in sync without manual exports."
    >
      {/* Capabilities */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl mb-10 zy-reveal">
            <h2 className="zy-h2">What the manufacturing module covers</h2>
            <p className="zy-body mt-4">
              Each capability below is already part of the connected Zynthoro ERP, not a bolt-on
              integration. The underlying data model is shared with purchasing, inventory,
              finance, HR and sales.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="zy-reveal p-6 rounded-2xl border bg-white"
                  style={{ borderColor: "#eee" }}
                  data-testid={`manufacturing-cap-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-4"
                    style={{ background: "#EAF0FF" }}
                  >
                    <Icon size={20} style={{ color: "#1A4FFF" }} />
                  </span>
                  <h3 className="text-[16px] font-bold tracking-tight mb-1.5">{c.title}</h3>
                  <p className="text-[13.5px] text-[#555] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Designed for European SME manufacturers</h2>
            <p className="zy-body mt-4">
              Zynthoro's manufacturing module is designed for producers that need real ERP
              behaviour — BOMs, lots, QC, production costs — without the cost or implementation
              weight of a traditional enterprise ERP.
            </p>
            <ul className="mt-6 space-y-2.5 zy-body">
              {INDUSTRIES.map((i) => (
                <li key={i} className="flex gap-2.5">
                  <span style={{ color: "#1A4FFF" }}>·</span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it fits */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">How manufacturing fits in the Zynthoro ERP ecosystem</h2>
            <p className="zy-body mt-4">
              A production order consumes raw materials from the shared stock ledger, generates
              finished goods and books the production cost to the correct finance account.
              Quality control results stay attached to the lot, so recalls, warranty claims and
              audits all draw from the same source of truth.
            </p>
            <p className="zy-body mt-4">
              The AI assistant layer can summarise production variance, flag quality trends and
              propose BOM optimisations across the same shared data model.
            </p>
          </div>
        </div>
      </section>

      {/* Answer-first FAQ */}
      <section className="zy-section" style={{ background: "#F6F8FB" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Manufacturing — common questions</h2>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Is Zynthoro a manufacturing ERP?</h3>
              <p className="zy-body mt-2">
                Zynthoro is an AI-native business operating system for SMEs. Manufacturing is one
                of its 12 connected modules, alongside finance, sales, HR, projects, operations
                and marketing.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Does Zynthoro support multi-level BOMs?</h3>
              <p className="zy-body mt-2">
                Yes. BOMs are versioned, support raw → semi-finished → finished structures and
                roll costs up at every level.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Which industries is the manufacturing module designed for?</h3>
              <p className="zy-body mt-2">
                Food &amp; beverage, cosmetics, pharma &amp; supplements and light manufacturing SMEs.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-[17px] font-bold tracking-tight">Is lot traceability included?</h3>
              <p className="zy-body mt-2">
                Yes. Lot numbers and expiry data flow from goods receipt through production and
                dispatch, with one-click recall queries across the chain.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related reading */}
      <section className="zy-section" style={{ background: "#fff" }}>
        <div className="zy-container">
          <div className="max-w-3xl zy-reveal">
            <h2 className="zy-h2">Related reading</h2>
            <ul className="mt-5 space-y-2.5 zy-body">
              <li><Link to="/blog/best-erp-for-manufacturing" className="zy-link">Best ERP for Manufacturing: A 2026 Buyer Guide for SMEs</Link></li>
              <li><Link to="/blog/erp-for-food-manufacturing" className="zy-link">ERP for Food Manufacturing: A Practical Guide for SMEs</Link></li>
              <li><Link to="/blog/manufacturing-traceability-software" className="zy-link">Manufacturing traceability software</Link></li>
              <li><Link to="/modules/supply-chain" className="zy-link">Supply chain module</Link> · <Link to="/modules/compliance" className="zy-link">Compliance module</Link></li>
            </ul>
          </div>
        </div>
      </section>
    </ModulePageLayout>
  );
}
